// Library imports
import Feature from 'ol/Feature';
import { Polygon, Polygon as OlPolygon } from 'ol/geom';
import { Overlay } from 'ol';
import { transform as olTransform } from 'ol/proj';
import OlMap from 'ol/Map';
import * as olSphere from 'ol/sphere';
// Local imports
import { DrawInteractionTool } from './tool-base';
import { getGeomLength, getPointsDistance } from '@openlayers/ol-utils';
import { boundsToGeoJson, searchToGeoJson, calcArea, findSimilar, getArea, getLocalCoordinatesInCcwOrder } from './tool-utils';
import { builNameLabel, buildAreaLabel, buildPerimeterLabel } from './tool-labels';


export class AreaTool extends DrawInteractionTool {
    public sourceProj = this.mapWrapper.getProjection();
    public map = this.mapWrapper.getInstance() as OlMap;
    lastClickCoords = [];
    
    onDrawStart(e) {
        // OlDrawEvent
        const currFeature: Feature = e.feature;
        this.geometryListener = this.bindEvents(currFeature);
        super.onDrawStart(e);
    }

    getLabelText(polygon, text?: string): string {
        let prefix: string = '';
        let area: number;

        const perimeterLength = getGeomLength(polygon, this.mapWrapper.getProjection());
        let perimeterOutput: string = '';

        const unit = kuura.getUnit();

        if (kuura.getProjectProj4Code() === 'EPSG:4326') {
            const toMetersSquared = unit.toMeters * unit.toMeters;
            prefix = '~';
            area = Math.abs(olSphere.getArea(polygon)) / toMetersSquared;
        } else {
            const sourceProj = this.mapWrapper.getProjection();
            const ccwLocalCoordinates = getLocalCoordinatesInCcwOrder(polygon, sourceProj);
            area = getArea(ccwLocalCoordinates);
        }

        perimeterOutput += prefix;
        if (unit.abbr === 'm' && perimeterLength > 1000) {
            perimeterOutput += `${Math.round(perimeterLength) / 1000} km`;
        } else {
            perimeterOutput += `${Math.round(perimeterLength * 100) / 100} ${unit.abbr}`;
        }

        if (unit.abbr === 'm' && area > 1000000) {
            return `${prefix}${Math.round((area / 1000000) * 100) / 100} km²`;
        }
        return `${prefix}${Math.round(area * 100) / 100} ${unit.abbr}² ${kuura.getMessage('js_perim')}: ${perimeterOutput}`;
    }

    restoreFromCoords(coords: Coordinate[][][], sourceUuid?: string): Feature[] {
        if (!Array.isArray(coords)) return;

        return coords.map(measurement => {
            const feature = new Feature({
                geometry: new OlPolygon(measurement),
            });
            feature.set('finished', true);
            feature.set('sourceUuid', sourceUuid);
            this.drawLayer.getSource().addFeature(feature);
            this.bindEvents(feature);
            this.drawSegmentLabels(feature); // careful not to use throttled draw with initial draw
            return feature;
        });
    }

    bindEvents = (feature): [any, Overlay] => {
        const evt = feature.getGeometry().on('change', e => {
            const geom = feature.getGeometry();
            const coords = geom.getCoordinates();

            if (this.lastClickCoords.length !== coords[0].length) {
                this.onDrawProgressCallback(feature);
            }
            this.lastClickCoords = coords[0];
            this.drawSegmentLabelsThrottled(feature);
        });

        feature.set('type', 'area-measurement');

        return evt;
    };

    addFeature(feature: Feature, showLabel: boolean = true, text?: string) {
        feature.set('finished', true);
        if (showLabel) {
            this.drawSegmentLabels(feature, text);
        }
        this.bindEvents(feature);
        this.drawLayer.getSource().addFeature(feature);
    }

    drawSegmentLabels = (feature: Feature, text?: string) => {
        if (!feature) return;
        this.clearFeatureLabels(feature);

        const geometry = feature.getGeometry() as Polygon;
        const coordinates = geometry.getCoordinates()[0];

        if (!coordinates || coordinates.length < 2) return;

        // total label
        if (coordinates.length > 3) {
            const totalText = this.getLabelText(geometry, text);
            const totalCoordinate = geometry.getInteriorPoint().getCoordinates();
            if (feature.get('finished')) {
                this.removeInProgressLabel();
                this.createLabelFeature(feature, totalText, totalCoordinate, false);
            } else {
                this.updateInProgressLabel(totalText, totalCoordinate, false);
            }
        }

        // segment labels
        if (this.showSegmentLabels && coordinates.length < 30) {
            const sourceProj = this.mapWrapper.getProjection();

            for (let i = 1; i < coordinates.length; i++) {
                const prevCoord = coordinates[i - 1];
                const currentCoord = coordinates[i];

                const segmentLength = getPointsDistance(
                    olTransform(prevCoord, sourceProj, kuura.getProjectProj4Code()),
                    olTransform(currentCoord, sourceProj, kuura.getProjectProj4Code())
                );
                const segmentText = this.formatLength(segmentLength);

                this.createLabelFeature(feature, segmentText, [(prevCoord[0] + currentCoord[0]) / 2, (prevCoord[1] + currentCoord[1]) / 2], true);
            }
        }
    }
    drawSegmentLabelsThrottled = _.throttle(this.drawSegmentLabels, 16);

    aiZoom = async (feature: Feature) => {   
        await boundsToGeoJson(this, feature);
    };

    findSimilar = async (feature: Feature) => {
        await findSimilar(this, feature);
    };
}
